'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { SliderInputProps } from '../lib/calculator.types';
import { TooltipLabel } from './TooltipLabel';

export const CustomSlider: React.FC<SliderInputProps & { hideInput?: boolean }> = ({
  field,
  value,
  onChange,
  disabled = false,
  hideInput = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const skipNextSyncRef = useRef(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  // Safety check for undefined field
  if (!field) {
    console.error('CustomSlider received undefined field prop');
    return null;
  }

  const min = field.min || 0;
  const max = field.max || 100;

  // Generate all valid step values for logarithmic sliders (for equal-step slider behavior)
  const validValues = useMemo(() => {
    if (field.sliderScale !== 'logarithmic') return null;

    const values: number[] = [];
    let current = min;

    while (current <= max) {
      values.push(current);

      // Determine step size based on current value
      let step: number;
      if (current < 10000) step = 500;
      else if (current < 25000) step = 2500;
      else if (current < 50000) step = 5000;
      else step = 10000;

      current += step;
    }

    // Ensure max value is included
    if (values[values.length - 1] !== max) {
      values.push(max);
    }

    return values;
  }, [field.sliderScale, min, max]);

  // Update local value when prop changes
  useEffect(() => {
    // Skip sync if we just manually updated in handleBlur
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    // Only sync from parent when not dragging AND not editing
    if (!isDragging && !isEditing) {
      setLocalValue(value);
    }
  }, [value, isDragging, isEditing]);

  // Calculate percentage position
  // For logarithmic sliders: use index-based positioning
  // For linear sliders: use standard linear positioning
  const percentage = useMemo(() => {
    if (validValues) {
      // Find closest valid value index
      const index = validValues.findIndex(v => v >= localValue);
      const actualIndex = index === -1 ? validValues.length - 1 : index;
      return (actualIndex / (validValues.length - 1)) * 100;
    }
    // Linear positioning for non-logarithmic sliders
    return ((localValue - min) / (max - min)) * 100;
  }, [localValue, validValues, min, max]);

  // Enter edit mode - show raw numeric value
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(true);
    setEditValue(localValue.toString());
    e.target.select(); // Select all on focus for easy replacement
  };

  // Update raw string as user types - no parsing, no syncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  // Exit edit mode - parse, validate, sync
  const handleBlur = () => {
    // Allow only digits and decimal point
    const cleaned = editValue.replace(/[^0-9.]/g, '');
    const parsed = parseFloat(cleaned);

    // Only update if valid and in range
    if (!isNaN(parsed) && parsed >= min && parsed <= max) {
      let clampedValue = Math.min(Math.max(parsed, min), max);

      // Apply step if specified - with dynamic stepping for logarithmic sliders
      let step = typeof field.step === 'number' ? field.step : 1;

      if (field.sliderScale === 'logarithmic') {
        if (clampedValue <= 10000) step = 500;
        else if (clampedValue <= 25000) step = 2500;
        else if (clampedValue <= 50000) step = 5000;
        else step = 10000;
      }

      clampedValue = Math.round(clampedValue / step) * step;
      clampedValue = Math.min(Math.max(clampedValue, min), max);

      // Prevent useEffect from overwriting this value before debounced update returns
      skipNextSyncRef.current = true;

      setLocalValue(clampedValue);
      onChange(clampedValue);
    }
    // If invalid input, do nothing - input reverts to formatted localValue

    setIsEditing(false);
  };

  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(true);

    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const x = clientX - rect.left;
      const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);

      let clampedValue: number;

      // Equal-step slider for logarithmic fields (index-based positioning)
      if (validValues) {
        // Convert percentage to index
        const targetIndex = Math.round((percentage / 100) * (validValues.length - 1));
        const boundedIndex = Math.min(Math.max(targetIndex, 0), validValues.length - 1);
        clampedValue = validValues[boundedIndex];
      } else {
        // Linear slider for standard fields
        const newValue = min + (percentage / 100) * (max - min);

        // Apply step if specified
        let step = typeof field.step === 'number' ? field.step : 1;
        const steppedValue = Math.round(newValue / step) * step;
        clampedValue = Math.min(Math.max(steppedValue, min), max);
      }

      // STATE DRIFT FIX: Update both local and parent state immediately
      setLocalValue(clampedValue);
      onChange(clampedValue);
    };

    const handleEnd = () => {
      setIsDragging(false);
      // STATE DRIFT FIX: Ensure final value is synchronized
      // The value is already updated in handleMove, no need for additional update
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }, [disabled, min, max, field.step, onChange, validValues]);

  // Track clicking disabled to prevent accidental value changes while scrolling

  return (
    <div className="slider-container">
      {/* Label and Input Row */}
      {!hideInput && field.label && (
        <div className="flex justify-between items-center mb-2">
          <div className="text-sm font-medium text-gray-700">
            {field.tooltip ? (
              <TooltipLabel
                label={field.label}
                tooltip={field.tooltip}
                drawerCta={field.drawerCta}
              />
            ) : (
              field.label
            )}
          </div>
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9]*"
            aria-label={field.label}
            className={`w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-right ${
              disabled ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300'
            }`}
            value={
              isEditing
                ? editValue
                : (field.type === 'currency'
                    ? `$${localValue.toLocaleString()}`
                    : field.type === 'percentage'
                    ? `${localValue}%`
                    : localValue.toLocaleString())
            }
            onFocus={handleFocus}
            onChange={handleInputChange}
            onBlur={handleBlur}
            disabled={disabled}
          />
        </div>
      )}
      
      {/* Slider Track */}
      <div 
        ref={sliderRef}
        className="relative h-6 flex items-center"
        role="slider"
        aria-label={field.label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={localValue}
      >
        {/* Track Background */}
        <div className={`absolute w-full h-2 rounded-full ${disabled ? 'bg-gray-100' : 'bg-gray-200'}`}>
          {/* Progress Bar */}
          <motion.div 
            className="absolute h-full rounded-full pointer-events-none"
            style={{ background: disabled ? '#d1d5db' : '#ff34a7' }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: isDragging ? 0 : 0.2 }}
          />
        </div>
        
        {/* Thumb - positioned absolutely within the track container */}
        <motion.div 
          ref={thumbRef}
          className={`absolute w-6 h-6 bg-white rounded-full shadow-md transition-transform ${
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:scale-110'
          }`}
          style={{ 
            left: `${percentage}%`,
            marginLeft: '-12px'
          }}
          animate={{ scale: isDragging ? 1.2 : 1 }}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        />
      </div>
      
      {/* Min/Max Value Display - Shows range endpoints only */}
      {!hideInput && (
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-500">
            {field.type === 'currency' 
              ? `$${min.toLocaleString()}` 
              : field.type === 'percentage'
              ? `${min}%`
              : min.toLocaleString()}
          </span>
          <span className="text-xs text-gray-500">
            {field.type === 'currency' 
              ? `$${max.toLocaleString()}` 
              : field.type === 'percentage'
              ? `${max}%`
              : max.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
};

// Export as SliderInput for backward compatibility
export const SliderInput = CustomSlider; 
